import BareClient, { BareResponseFetch } from "@mercuryworkshop/bare-mux";
import { MessageW2C, VortexServiceWorker } from "@/worker";
import { renderError } from "@/worker/error";
import { FakeServiceWorker } from "@/worker/fakesw";
import { CookieStore } from "@/shared/cookie";
import { getSiteDirective } from "@/shared/security/siteTests";
import {
	initializeTracker,
	updateTracker,
	cleanTracker,
	getMostRestrictiveSite,
	storeReferrerPolicy,
	getReferrerPolicy,
} from "@/shared/security/forceReferrer";
import { unrewriteBlob, unrewriteUrl, type URLMeta } from "@rewriters/url";
import { rewriteJs } from "@rewriters/js";
import { VortexHeaders } from "@/shared/headers";
import { config, flagEnabled } from "@/shared";
import { rewriteHeaders } from "@rewriters/headers";
import { rewriteHtml } from "@rewriters/html";
import { rewriteCss } from "@rewriters/css";
import { rewriteWorkers } from "@rewriters/worker";
import { VortexDownload } from "@client/events";

// ── ENHANCED: New systems ─────────────────────────────────────────────────
import { getCachedRewrite, setCachedRewrite, isCacheable, pruneCache } from "@/worker/cache";
import { schedulePrefetch } from "@/worker/prefetch";

let requestCount = 0;
function maybePruneCache() {
	if (++requestCount % 50 === 0) pruneCache();
}
// ─────────────────────────────────────────────────────────────────────────

function isRedirect(response: BareResponseFetch) {
	return response.status >= 300 && response.status < 400;
}

function isDownload(responseHeaders: object, destination: string): boolean {
	if (["document", "iframe"].includes(destination)) {
		const header = responseHeaders["content-disposition"];
		if (header) {
			return header !== "inline";
		} else {
			const displayableMimes = [
				"text/html","text/plain","text/css","text/javascript","text/xml",
				"application/javascript","application/json","application/xml","application/pdf",
			];
			const contentType = responseHeaders["content-type"]?.split(";")[0].trim().toLowerCase();
			if (contentType && !displayableMimes.includes(contentType) &&
				!contentType.startsWith("text") && !contentType.startsWith("image") &&
				!contentType.startsWith("font") && !contentType.startsWith("video")) {
				return true;
			}
		}
	}
	return false;
}

// ── ENHANCED: Streaming HTML rewriter ─────────────────────────────────────
function createStreamingHtmlRewriter(
	cookieStore: CookieStore, meta: URLMeta, fromTop: boolean
): TransformStream<Uint8Array, Uint8Array> {
	const decoder = new TextDecoder();
	const encoder = new TextEncoder();
	let accumulated = "";
	return new TransformStream({
		transform(chunk) { accumulated += decoder.decode(chunk, { stream: true }); },
		flush(controller) {
			try {
				controller.enqueue(encoder.encode(rewriteHtml(accumulated, cookieStore, meta, fromTop)));
			} catch {
				controller.enqueue(encoder.encode(accumulated));
			}
			controller.terminate();
		},
	});
}
// ─────────────────────────────────────────────────────────────────────────

export async function handleFetch(
	this: VortexServiceWorker,
	request: Request,
	client: Client | null
) {
	maybePruneCache();
	try {
		const requestUrl = new URL(request.url);

		if (requestUrl.pathname === this.config.files.wasm) {
			return fetch(this.config.files.wasm).then(async (x) => {
				const buf = await x.arrayBuffer();
				const b64 = btoa(new Uint8Array(buf).reduce((data, byte) => (data.push(String.fromCharCode(byte)), data), []).join(""));
				let payload = "if ('document' in self && document.currentScript) { document.currentScript.remove(); }\n";
				payload += `self.WASM = '${b64}';`;
				return new Response(payload, { headers: { "content-type": "text/javascript" } });
			});
		}

		let scriptType = "";
		let topFrameName;
		let parentFrameName;
		const extraParams: Record<string, string> = {};

		for (const [param, value] of [...requestUrl.searchParams.entries()]) {
			switch (param) {
				case "type": scriptType = value; break;
				case "dest": break;
				case "topFrame": topFrameName = value; break;
				case "parentFrame": parentFrameName = value; break;
				default:
					dbg.warn(`${requestUrl.href} extraneous query parameter ${param}. Assuming <form> element`);
					extraParams[param] = value;
					break;
			}
			requestUrl.searchParams.delete(param);
		}

		// Fix: handle the null.html?https://... pattern where the target URL
		// ends up as a query param instead of being proxied directly.
		// This happens when sites construct script URLs using null.html as a base.
		let unrewritten = unrewriteUrl(requestUrl);
		if (!unrewritten || unrewritten === "") {
			// Try extracting a URL from the raw search string
			const rawSearch = requestUrl.search.slice(1);
			try {
				const candidate = decodeURIComponent(rawSearch);
				if (candidate.startsWith("http://") || candidate.startsWith("https://")) {
					unrewritten = candidate;
				}
			} catch { /* ignore decode errors */ }
		}
		if (!unrewritten || unrewritten === "") {
			return new Response(undefined, { status: 404 });
		}
		const url = new URL(unrewritten);
		for (const [param, value] of Object.entries(extraParams)) url.searchParams.set(param, value);

		const meta: URLMeta = { origin: url, base: url, topFrameName, parentFrameName };

		if (requestUrl.pathname.startsWith(`${this.config.prefix}blob:`) || requestUrl.pathname.startsWith(`${this.config.prefix}data:`)) {
			let dataUrl = requestUrl.pathname.substring(this.config.prefix.length);
			if (dataUrl.startsWith("blob:")) dataUrl = unrewriteBlob(dataUrl);
			const response: Partial<BareResponseFetch> = await fetch(dataUrl, {});
			response.finalURL = dataUrl.startsWith("blob:") ? dataUrl : "(data url)";
			let body: BodyType;
			if (response.body) body = await rewriteBody(response as BareResponseFetch, meta, request.destination, scriptType, this.cookieStore);
			const headers = Object.fromEntries(response.headers.entries());
			if (crossOriginIsolated) {
				headers["Cross-Origin-Opener-Policy"] = "same-origin";
				headers["Cross-Origin-Embedder-Policy"] = "require-corp";
			}
			return new Response(body, { status: response.status, statusText: response.statusText, headers });
		}

		const activeWorker: FakeServiceWorker | null = this.serviceWorkers.find((w) => w.origin === url.origin);
		if (activeWorker?.connected && requestUrl.searchParams.get("from") !== "swruntime") {
			const r = await activeWorker.fetch(request);
			if (r) return r;
		}
		if (url.origin === new URL(request.url).origin) {
			throw new Error("attempted to fetch from same origin - this means the site has obtained a reference to the real origin, aborting");
		}

		const headers = new VortexHeaders();
		for (const [key, value] of request.headers.entries()) headers.set(key, value);

		if (client && new URL(client.url).pathname.startsWith(config.prefix)) {
			const clientURL = new URL(unrewriteUrl(client.url));
			if (!clientURL.toString().includes("youtube.com")) {
				headers.set("Referer", clientURL.href);
				headers.set("Origin", clientURL.origin);
			}
		}

		const cookies = this.cookieStore.getCookies(url, false);
		if (cookies.length) headers.set("Cookie", cookies);

		let isTopLevelProxyNavigation = false;
		if (request.destination === "iframe" && request.mode === "navigate" && request.referrer && request.referrer !== "no-referrer" && request.referrer !== location.origin + config.prefix + "no-referrer") {
			let currentReferrer = request.referrer;
			const allClients = await self.clients.matchAll({ type: "window" });
			while (currentReferrer) {
				if (!currentReferrer.includes(config.prefix)) { isTopLevelProxyNavigation = true; break; }
				const parentChainClient = allClients.find((c) => c.url === currentReferrer);
				const parentPolicyData = await getReferrerPolicy(currentReferrer);
				if (!parentPolicyData || !parentPolicyData.referrer) {
					if (parentChainClient && currentReferrer.startsWith(location.origin)) isTopLevelProxyNavigation = true;
					break;
				}
				if (parentChainClient && parentChainClient.frameType === "nested") currentReferrer = parentPolicyData.referrer;
				else break;
			}
		}

		if (isTopLevelProxyNavigation) { headers.set("Sec-Fetch-Dest", "document"); headers.set("Sec-Fetch-Mode", "navigate"); }
		else { headers.set("Sec-Fetch-Dest", request.destination || "empty"); headers.set("Sec-Fetch-Mode", request.mode); }

		let siteDirective = "none";
		if (request.referrer && request.referrer !== "" && request.referrer !== "no-referrer" && request.referrer !== location.origin + config.prefix + "no-referrer") {
			if (request.referrer.includes(config.prefix)) {
				const unrewrittenReferrer = unrewriteUrl(request.referrer);
				if (unrewrittenReferrer) siteDirective = await getSiteDirective(meta, new URL(unrewrittenReferrer), this.client);
			}
		}

		await initializeTracker(url.toString(), request.referrer ? unrewriteUrl(request.referrer) : null, siteDirective);
		headers.set("Sec-Fetch-Site", await getMostRestrictiveSite(url.toString(), siteDirective));

		const ev = new VortexRequestEvent(url, headers.headers, request.body, request.method, request.destination, client);
		this.dispatchEvent(ev);

		const response = (await ev.response) || ((await this.client.fetch(ev.url, {
			method: ev.method, body: ev.body, headers: ev.requestHeaders,
			credentials: "omit", mode: request.mode === "cors" ? request.mode : "same-origin",
			cache: request.cache, redirect: "manual", duplex: "half",
		} as RequestInit)) as BareResponseFetch);
		response.finalURL = ev.url.href;

		return await handleResponse(url, meta, scriptType, request.destination, request.mode, response, this.cookieStore, client, this.client, this, request.referrer);
	} catch (err) {
		const errorDetails = { message: err.message, url: request.url, destination: request.destination };
		if (err.stack) errorDetails["stack"] = err.stack;
		console.error("ERROR FROM SERVICE WORKER FETCH: ", errorDetails);
		console.error(err);
		if (!["document", "iframe"].includes(request.destination)) return new Response(undefined, { status: 500 });
		const formattedError = Object.entries(errorDetails).map(([key, value]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`).join("\n\n");
		return renderError(formattedError, unrewriteUrl(request.url));
	}
}

async function handleResponse(
	url: URL, meta: URLMeta, scriptType: string, destination: RequestDestination,
	mode: RequestMode, response: BareResponseFetch, cookieStore: CookieStore,
	client: Client, bareClient: BareClient, swtarget: VortexServiceWorker, referrer: string
): Promise<Response> {
	let responseBody: BodyType;

	const isNavigationRequest = mode === "navigate" && ["document", "iframe"].includes(destination);
	const responseHeaders = await rewriteHeaders(response.rawHeaders, meta, bareClient, { get: getReferrerPolicy, set: storeReferrerPolicy });

	if (isNavigationRequest && responseHeaders["referrer-policy"] && referrer) {
		await storeReferrerPolicy(url.href, responseHeaders["referrer-policy"], referrer);
	}

	if (isRedirect(response)) {
		// Guard: location header may be absent on some redirects
		const rawLocation = responseHeaders["location"];
		const unrewrittenLocation = rawLocation ? unrewriteUrl(rawLocation) : null;
		if (!unrewrittenLocation) {
			// No usable location — just fall through without crashing
			delete responseHeaders["location"];
		} else {
		const redirectUrl = new URL(unrewrittenLocation);
		await updateTracker(url.toString(), redirectUrl.toString(), responseHeaders["referrer-policy"]);
		const redirectMeta = { origin: redirectUrl, base: redirectUrl };
		const newSiteDirective = await getSiteDirective(redirectMeta, url, bareClient);
		await getMostRestrictiveSite(redirectUrl.toString(), newSiteDirective);
		if (scriptType) {
				const u = new URL(responseHeaders["location"]);
				u.searchParams.set("type", scriptType);
				responseHeaders["location"] = u.href;
			}
		} // end else (unrewrittenLocation valid)
	}

	const maybeHeaders = responseHeaders["set-cookie"] || [];
	for (const cookie in maybeHeaders) {
		if (client) {
			const promise = swtarget.dispatch(client, { vortex$type: "cookie", cookie, url: url.href });
			if (destination !== "document" && destination !== "iframe") await promise;
		}
	}
	await cookieStore.setCookies(maybeHeaders instanceof Array ? maybeHeaders : [maybeHeaders], url);
	for (const header in responseHeaders) {
		if (Array.isArray(responseHeaders[header])) responseHeaders[header] = responseHeaders[header][0];
	}

	if (isDownload(responseHeaders, destination) && !isRedirect(response)) {
		if (flagEnabled("interceptDownloads", url)) {
			if (!client) throw new Error("cant find client");
			let filename: string | null = null;
			const disp = responseHeaders["content-disposition"];
			if (typeof disp === "string") {
				const filenameMatch = disp.match(/filename=["']?([^"';\n]*)["']?/i);
				if (filenameMatch && filenameMatch[1]) filename = filenameMatch[1];
			}
			let clis = await clients.matchAll({});
			clis = clis.filter((e) => !e.url.includes(config.prefix));
			if (clis.length < 1) throw Error("couldn't find a controller client to dispatch download to");
			const download: VortexDownload = {
				filename, url: url.href, type: responseHeaders["content-type"],
				body: response.body, length: Number(responseHeaders["content-length"]),
			};
			clis[0].postMessage({ vortex$type: "download", download } as MessageW2C, [response.body]);
			await new Promise(() => {});
		} else {
			const header = responseHeaders["content-disposition"];
			if (!/\s*?((inline|attachment);\s*?)filename=/i.test(header)) {
				const type = /^\s*?attachment/i.test(header) ? "attachment" : "inline";
				const [filename] = new URL(response.finalURL).pathname.split("/").slice(-1);
				responseHeaders["content-disposition"] = `${type}; filename=${JSON.stringify(filename)}`;
			}
		}
	}

	if (response.body && !isRedirect(response)) {
		// ── ENHANCED: Cache check ───────────────────────────────────────────
		if (isCacheable(destination)) {
			const etagRaw = response.rawHeaders?.["etag"];
			const etag: string | null = Array.isArray(etagRaw) ? etagRaw[0] : (etagRaw || null);
			const lastModRaw = response.rawHeaders?.["last-modified"];
			const lastMod: string | null = Array.isArray(lastModRaw) ? lastModRaw[0] : (lastModRaw || null);
			const cached = await getCachedRewrite(url.href, etag, lastMod, destination);
			if (cached) {
				const cachedHeaders = Object.fromEntries(cached.headers.entries());
				delete cachedHeaders["x-vortex-cached"];
				delete cachedHeaders["x-vortex-url"];
				return new Response(await cached.arrayBuffer(), {
					headers: { ...responseHeaders, ...cachedHeaders },
					status: response.status, statusText: response.statusText,
				});
			}
		}
		// ───────────────────────────────────────────────────────────────────

		responseBody = await rewriteBody(response, meta, destination, scriptType, cookieStore);

		// ── ENHANCED: Store in cache ────────────────────────────────────────
		if (isCacheable(destination) && responseBody && !(responseBody instanceof ReadableStream)) {
			const etagRaw = response.rawHeaders?.["etag"];
			const etag: string | null = Array.isArray(etagRaw) ? etagRaw[0] : (etagRaw || null);
			const lastModRaw = response.rawHeaders?.["last-modified"];
			const lastMod: string | null = Array.isArray(lastModRaw) ? lastModRaw[0] : (lastModRaw || null);
			setCachedRewrite(url.href, etag, lastMod, destination, responseBody as ArrayBuffer | string, responseHeaders).catch(() => {});
		}
		// ── ENHANCED: Schedule prefetch ─────────────────────────────────────
		if (["document", "iframe"].includes(destination) && typeof responseBody === "string") {
			schedulePrefetch(responseBody, meta);
		}
		// ───────────────────────────────────────────────────────────────────
	}

	if (responseHeaders["accept"] === "text/event-stream") responseHeaders["content-type"] = "text/event-stream";
	delete responseHeaders["permissions-policy"];

	if (crossOriginIsolated && ["document","iframe","worker","sharedworker","style","script"].includes(destination)) {
		responseHeaders["Cross-Origin-Embedder-Policy"] = "require-corp";
		responseHeaders["Cross-Origin-Opener-Policy"] = "same-origin";
	}

	const ev = new VortexHandleResponseEvent(responseBody, responseHeaders, response.status, response.statusText, destination, url, response, client);
	swtarget.dispatchEvent(ev);
	if (!isRedirect(response)) await cleanTracker(url.toString());

	return new Response(ev.responseBody, { headers: ev.responseHeaders as HeadersInit, status: ev.status, statusText: ev.statusText });
}

async function rewriteBody(
	response: BareResponseFetch, meta: URLMeta, destination: RequestDestination,
	workertype: string, cookieStore: CookieStore
): Promise<BodyType> {
	switch (destination) {
		case "iframe":
		case "document": {
			const contentType = response.headers.get("content-type") ?? "";
			if (contentType.startsWith("text/html")) {
				// ── ENHANCED: Streaming HTML rewrite ─────────────────────
				if (response.body) {
					return response.body.pipeThrough(createStreamingHtmlRewriter(cookieStore, meta, true));
				}
				// ─────────────────────────────────────────────────────────
				return rewriteHtml(await response.text(), cookieStore, meta, true);
			}
			// Non-HTML document/iframe response (e.g. PDF, binary) — pass through raw
			return response.body;
		}
		case "script":
			return rewriteJs(new Uint8Array(await response.arrayBuffer()), response.finalURL, meta, workertype === "module") as unknown as ArrayBuffer;
		case "style":
			return rewriteCss(await response.text(), meta);
		case "sharedworker":
		case "worker":
			return rewriteWorkers(new Uint8Array(await response.arrayBuffer()), workertype, response.finalURL, meta);
		default:
			return response.body;
	}
}

type BodyType = string | ArrayBuffer | Blob | ReadableStream<any>;

export class VortexHandleResponseEvent extends Event {
	constructor(
		public responseBody: BodyType, public responseHeaders: Record<string, string>,
		public status: number, public statusText: string, public destination: string,
		public url: URL, public rawResponse: BareResponseFetch, public client: Client
	) { super("handleResponse"); }
}

export class VortexRequestEvent extends Event {
	constructor(
		public url: URL, public requestHeaders: Record<string, string>,
		public body: BodyType, public method: string,
		public destination: string, public client: Client
	) { super("request"); }
	public response?: BareResponseFetch | Promise<BareResponseFetch>;
}
